import {
  baseContour,
  type WSIPassportType,
  type AIHandlerResultType,
  type MeasurementsType,
} from '@tig-dpqa-cloud/contract-backend-dataplane';
import type {
  GeoJSONContourType,
  xyCoordinates,
  SlideScanGeneratedMetadataType,
} from '@tig-dpqa-cloud/contract-backend-frontend';
import { match, P } from 'ts-pattern';
import z from 'zod';

const measurementContours = z.array(baseContour).optional().describe('List of contours measured by the AI handler');
type MeasurementContoursType = z.infer<typeof measurementContours>;

type XYContoursGroupType = z.infer<z.ZodArray<typeof xyCoordinates>>;

const generateGeoJsonFromContours = (
  contours: MeasurementContoursType,
  offset: { x: number; y: number } | undefined = undefined,
): GeoJSONContourType[] | undefined => {
  if (!contours) {
    return undefined;
  }
  return Object.values(
    contours.reduce((acc, curr, index) => {
      const idx = curr.hierarchy[3] === -1 ? index : curr.hierarchy[3];
      if (!(idx in acc)) {
        acc[idx] = [];
      }
      acc[idx].push(offset ? curr.contour.map((c) => [Math.max(0, c[0] + offset.x), Math.max(0, c[1] + offset.y)]) : curr.contour);
      return acc;
    }, {} as { [parentIdx: string]: XYContoursGroupType[] }),
  ).map((c) => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: c,
    },
  }));
};

const determineMacroShift = (metadata: WSIPassportType): { x: number; y: number } | undefined => {
  return match(metadata)
    .with(
      {
        macro: {
          croppedArea: P.select('cropped', P.not(P.nullish)),
          originalArea: P.select('original', P.not(P.nullish)),
        },
      },
      ({ cropped, original }) => ({
        x: original.topLeft.x - cropped.topLeft.x,
        y: 0,
      }),
    )
    .with({ macroOffset: P.select('offset', P.not(P.nullish)) }, ({ offset }) => offset)
    .otherwise(() => undefined);
};

export const generateMetadata = (
  aiInfo: AIHandlerResultType,
  metadata: WSIPassportType,
): SlideScanGeneratedMetadataType => {
  return match(aiInfo.schema_version)
    .with(P.union('3.0.0', '3.1.0', '3.2.0', '3.3.0'), () => {
      const { measurements } = aiInfo as { measurements: MeasurementsType };
      return {
        schema_version: '1.0.0',
        contours: {
          blur: generateGeoJsonFromContours(measurementContours.parse(measurements?.blur?.contours)),
          missing_tissue: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.missing_tissue?.contours),
            determineMacroShift(metadata),
          ),
          tissue: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.tissue?.contours),
          ),
          misaligned_cover_slip: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.misaligned_cover_slip?.contours),
          ),
          cut_off_tissue_macro: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.cut_off_tissue?.contours_macro),
            determineMacroShift(metadata),
          ),
          cut_off_tissue_wsi: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.cut_off_tissue?.contours_wsi),
          ),
          marker_on_top_of_tissue: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.marker_on_top_of_tissue?.contours),
          ),
        },
        patches: {
          ...(aiInfo.measurements?.blur ? { blur: aiInfo.measurements.blur.patches } : {}),
        },
        missing_tissue_bounding_box: generateGeoJsonFromContours(
          measurementContours.parse(measurements?.missing_tissue?.processed_region_contours),
          determineMacroShift(metadata),
        ),
      } satisfies SlideScanGeneratedMetadataType;
    })
    .otherwise(() => { throw new Error(`Unsupported AI info schema version: ${aiInfo.schema_version}`); });
};
